import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, CheckCircle2, Clock, Loader2 } from 'lucide-react';
import { formatStunden } from '../../services/bestandteile/stundenLogic';
import {
    STUNDEN_EINGABE_UNGUELTIG, stundenFensterOffen, stundenFensterSicht, type StundenAbfrage, type StundenFensterZeile,
} from './abschlussLogic';
import type { StundenPflicht } from './useBestandteilAbschluss';

/**
 * PFLICHTFENSTER FUER DIE STUNDEN nach einer Modul-Ausgabe (Stundenkontingent S3).
 *
 * Hat eine Freigabe bzw. ein Versand Bestandteile einer Paketposition erledigt
 * (Trigger aus Nr. 166 — ohne Stunden), fragt dieses Fenster direkt danach je
 * Bestandteil die geleisteten Stunden ab. Betreiber: „Stunden eintragen ist
 * Pflicht" — deshalb:
 *  - kein Schliessen-Kreuz, kein „Überspringen", kein ESC, kein Klick daneben;
 *  - „Speichern" erst, wenn JEDES Feld eine gueltige Stundenzahl hat
 *    (`parseStunden`: Komma oder Punkt, > 0 bis 9999, zwei Nachkommastellen);
 *  - erst nach einem FEHLVERSUCH (Antwort der Datenbank oder Netz) zusaetzlich
 *    „Später nachtragen" — die Meldung zeigt dann gelb „Stunden fehlen".
 *
 * Zeichnen: je Modul an GENAU einer Stelle (`pflicht={abschluss.stundenPflicht}`),
 * per Portal an `document.body` — so liegt es ueber jedem Modul-Dialog
 * (z-[220] > Bestandteile-Dialog 210 > Abrechnungsdialog 200 > Modul-Dialoge 150/160),
 * egal wo im Baum es steht. Der Zustand wohnt im Hook
 * (`useBestandteilAbschluss`), hier nur die Eingaben.
 *
 * Stunden sind intern (S8): keine Preise, nichts davon auf Rechnung.
 */

const ZeilenStand: React.FC<{ z: StundenFensterZeile }> = ({ z }) => {
    if (z.status === 'gespeichert') {
        return (
            <p className="mt-1 flex items-start gap-1.5 text-xs text-emerald-800">
                <CheckCircle2 className="mt-px h-3.5 w-3.5 shrink-0 text-emerald-600" />
                <span>{z.stunden !== null ? `${formatStunden(z.stunden)} gespeichert` : 'Gespeichert'}</span>
            </p>
        );
    }
    if (z.status === 'bereits') {
        return (
            <p className="mt-1 flex items-start gap-1.5 text-xs text-emerald-800">
                <CheckCircle2 className="mt-px h-3.5 w-3.5 shrink-0 text-emerald-600" />
                <span className="min-w-0 break-words">
                    {z.meldung}{z.stunden !== null ? ` (${formatStunden(z.stunden)})` : ''}
                </span>
            </p>
        );
    }
    // hinfaellig: hier gibt es nichts mehr einzutragen.
    return (
        <p className="mt-1 flex items-start gap-1.5 text-xs text-amber-800">
            <AlertTriangle className="mt-px h-3.5 w-3.5 shrink-0 text-amber-600" />
            <span className="min-w-0 break-words">{z.meldung}</span>
        </p>
    );
};

const Fenster: React.FC<{ abfrage: StundenAbfrage; pflicht: StundenPflicht }> = ({ abfrage, pflicht }) => {
    /** Eingabetext je Eintrag — bleibt nach einem Fehlversuch stehen. */
    const [eingaben, setEingaben] = useState<Record<string, string>>({});
    const sicht = stundenFensterSicht(abfrage, eingaben);
    const mehrere = sicht.zeilen.length > 1;
    const ersteFeldId = sicht.zeilen.find(z => z.mitFeld)?.eintragId ?? null;

    const speichern = (e?: React.FormEvent) => {
        e?.preventDefault();
        if (!sicht.speichernAktiv) return;
        const werte: Record<string, number> = {};
        for (const z of sicht.zeilen) if (z.mitFeld && z.wert !== null) werte[z.eintragId] = z.wert;
        void pflicht.speichern(werte);
    };

    return (
        <div
            className="fixed inset-0 z-[220] flex items-end justify-center bg-black/60 animate-fade-in sm:items-center sm:p-4 print:hidden"
            role="dialog"
            aria-modal="true"
            aria-labelledby="bestandteil-stunden-pflicht-titel"
            // Pflicht (S3): ESC schliesst nichts — auch keinen Dialog darunter.
            onKeyDown={e => { if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); } }}
        >
            <form
                onSubmit={speichern}
                noValidate
                className="flex max-h-[94vh] w-full flex-col overflow-hidden rounded-t-2xl bg-slate-50 shadow-2xl animate-scale-in sm:max-w-lg sm:rounded-2xl"
            >
                <div className="shrink-0 bg-slate-900 px-4 py-3 text-white sm:px-5 sm:py-4">
                    <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                        <Clock className="h-3.5 w-3.5" /> Stunden eintragen · Pflicht
                    </div>
                    <h3 id="bestandteil-stunden-pflicht-titel" className="mt-0.5 text-base font-semibold leading-snug">
                        {mehrere ? 'Bestandteile erledigt — wie viele Stunden?' : 'Bestandteil erledigt — wie viele Stunden?'}
                    </h3>
                </div>

                <div className="flex-1 space-y-3 overflow-y-auto p-3 sm:p-5">
                    <p className="text-xs leading-relaxed text-slate-600">
                        Die Ausgabe hat {mehrere ? 'diese Bestandteile' : 'diesen Bestandteil'} erledigt. Bitte die dafür
                        geleisteten Stunden eintragen — sie zählen gegen das Stundenkontingent der Position. Intern:
                        Stunden stehen nie auf Rechnung oder Leistungsnachweis.
                    </p>

                    {sicht.zeilen.map(z => {
                        const feldId = `bestandteil-stunden-${z.eintragId}`;
                        return (
                            <div key={z.eintragId} className="rounded-xl border border-slate-200 bg-white p-3">
                                {z.mitFeld ? (
                                    <>
                                        <label htmlFor={feldId} className="block break-words text-sm font-semibold text-slate-800">
                                            {z.titel}
                                        </label>
                                        <div className="mt-2 flex items-center gap-2">
                                            <input
                                                id={feldId}
                                                type="text"
                                                inputMode="decimal"
                                                autoComplete="off"
                                                autoFocus={z.eintragId === ersteFeldId}
                                                required
                                                aria-required="true"
                                                aria-invalid={z.ungueltig || z.status === 'fehler' ? 'true' : undefined}
                                                placeholder="z. B. 7,5"
                                                value={z.eingabe}
                                                disabled={sicht.speichert}
                                                onChange={e => {
                                                    const wert = e.target.value;
                                                    setEingaben(alt => ({ ...alt, [z.eintragId]: wert }));
                                                }}
                                                className={`min-h-11 w-full rounded-lg border bg-white px-3 text-sm text-slate-900 outline-none focus:ring-2 sm:min-h-9 disabled:bg-slate-100 ${
                                                    z.ungueltig || z.status === 'fehler'
                                                        ? 'border-rose-300 focus:ring-rose-200'
                                                        : 'border-slate-300 focus:ring-slate-300'
                                                }`}
                                            />
                                            <span className="shrink-0 text-sm font-semibold text-slate-500">h</span>
                                        </div>
                                        {z.ungueltig && <p className="mt-1 text-xs text-rose-700">{STUNDEN_EINGABE_UNGUELTIG}</p>}
                                        {z.status === 'fehler' && z.meldung && (
                                            <p className="mt-1 break-words text-xs text-rose-700" role="alert">{z.meldung}</p>
                                        )}
                                    </>
                                ) : (
                                    <>
                                        <p className="break-words text-sm font-semibold text-slate-800">{z.titel}</p>
                                        <ZeilenStand z={z} />
                                    </>
                                )}
                            </div>
                        );
                    })}

                    {sicht.spaeterErlaubt && (
                        <p className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                            <AlertTriangle className="mt-px h-3.5 w-3.5 shrink-0 text-amber-600" />
                            <span>
                                Das Speichern ist gescheitert. Erneut versuchen — oder „Später nachtragen“: dann zeigt der
                                Bestandteil gelb „Stunden fehlen“, nachzutragen im Bestandteile-Dialog.
                            </span>
                        </p>
                    )}
                </div>

                <div className="flex shrink-0 flex-col-reverse gap-2 border-t border-slate-200 bg-white p-3 sm:flex-row sm:justify-end sm:p-4">
                    {sicht.spaeterErlaubt && (
                        <button
                            type="button"
                            onClick={pflicht.spaeter}
                            className="min-h-11 w-full rounded-lg border border-slate-300 bg-white px-4 text-sm font-semibold text-slate-700 hover:bg-slate-50 sm:min-h-9 sm:w-auto"
                        >
                            Später nachtragen
                        </button>
                    )}
                    <button
                        type="submit"
                        disabled={!sicht.speichernAktiv}
                        className="inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-lg bg-slate-900 px-4 text-sm font-bold text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50 sm:min-h-9 sm:w-auto"
                    >
                        {sicht.speichert && <Loader2 className="h-4 w-4 animate-spin" />}
                        {sicht.speichert ? 'Speichert …' : 'Speichern'}
                    </button>
                </div>
            </form>
        </div>
    );
};

/**
 * Das Fenster — nur, solange die Abfrage Stunden braucht und nicht „Später
 * nachtragen" gewaehlt ist. Je Abfrage ein frischer Eingabestand (`key`).
 */
export const BestandteilStundenPflicht: React.FC<{ pflicht: StundenPflicht | null | undefined }> = ({ pflicht }) => {
    const abfrage = pflicht?.abfrage ?? null;
    if (!pflicht || !abfrage || !stundenFensterOffen(abfrage)) return null;
    const inhalt = <Fenster key={abfrage.id} abfrage={abfrage} pflicht={pflicht} />;
    // Portal an body: ueber jedem Modul-Dialog, unabhaengig von Stapelkontexten
    // des Moduls. Ohne DOM (Server-Markup im Test) steht es an Ort und Stelle.
    return typeof document !== 'undefined' && document.body ? createPortal(inhalt, document.body) : inhalt;
};
